// components/shared/EmptyState.js
// מצב ריק — אייקון + כותרת + הודעה + פעולה
// שימוש:
//   <EmptyState type="noData" />
//   <EmptyState type="noResults" />
//   <EmptyState
//     icon={<AddIcon />}
//     title="אין יחידות"
//     message="צור יחידות כדי להתחיל"
//     actionLabel="הוסף יחידה"
//     onAction={() => setOpenDialog(true)}
//   />

import { Box, Typography, Button } from '@mui/material';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';

const TYPE_DEFAULTS = {
  noData: {
    icon: InboxIcon,
    titleKey: 'emptyStates.noData',
    messageKey: 'emptyStates.getStarted',
  },
  noResults: {
    icon: SearchOffIcon,
    titleKey: 'emptyStates.noResults',
    messageKey: 'emptyStates.tryDifferentFilters',
  },
  error: {
    icon: ErrorOutlineIcon,
    titleKey: 'emptyStates.somethingWentWrong',
    messageKey: 'emptyStates.tryAgain',
  },
};

export default function EmptyState({
  type = 'noData',
  icon = null,
  title = null,
  message = null,
  actionLabel = null,
  onAction = null,
  sx = {},
}) {
  const { t } = useTranslation();
  const defaults = TYPE_DEFAULTS[type] || TYPE_DEFAULTS.noData;
  const IconComponent = icon || defaults.icon;

  return (
    <Box
      data-testid="shared__empty-state"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 3,
        ...sx,
      }}
    >
      {typeof IconComponent === 'function' ? (
        <IconComponent
          sx={{
            fontSize: 64,
            color: 'text.disabled',
            mb: 2,
            opacity: 0.6,
          }}
        />
      ) : (
        <Box sx={{ mb: 2, color: 'text.disabled', opacity: 0.6 }}>
          {IconComponent}
        </Box>
      )}

      <Typography
        variant="h6"
        color="text.secondary"
        gutterBottom
        data-testid="shared__empty-state__title"
      >
        {title || t(defaults.titleKey)}
      </Typography>

      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ maxWidth: 400, mb: onAction ? 3 : 0 }}
        data-testid="shared__empty-state__message"
      >
        {message || t(defaults.messageKey)}
      </Typography>

      {onAction && actionLabel && (
        <Button
          data-testid="shared__empty-state__button--action"
          variant="contained"
          onClick={onAction}
          size="medium"
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}
